import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { League, PlayerRecentStat } from '@/types/api.generated';
import { nflFullSeason } from '@/utils/league';

export const MAX_STATS = 5;
export const EMPTY_STAT = {
    value: undefined,
    date: null,
    matchupDescription: '--',
};

export const hasPlayerStatsAvailable = ({ projections }: { projections: PlayerProjectionFragment[] }) =>
    projections.every(projection => (projection?.playerRecentStats?.stats?.length ?? 0) > 0);

/*
 * Determines whether recent player stats should be displayed.
 *
 * Recent stats are shown only if:
 * 1. The player has recent stats available in the given projections group.
 * 2. The player is not an NFL full-season player.
 *
 * @param {Object} params - Function parameters.
 * @param {PlayerProjectionFragment[]} params.projectionsGroup - The array of player projections to check for available stats.
 * @param {League} params.league - The league the player belongs to (used to check for NFL full-season players).
 * @returns {boolean} True if recent player stats should be shown, false otherwise.
 */
export const getShouldShowPlayerRecentStats = ({
    projectionsGroup,
    league,
}: {
    projectionsGroup: PlayerProjectionFragment[];
    league: League;
}) => {
    const hasRecentPlayerStatsAvailable = hasPlayerStatsAvailable({ projections: projectionsGroup });
    const nflFullSeasonPlayer = nflFullSeason(league);
    // For NFL Full Season, we don't show recent stats for full season players
    return hasRecentPlayerStatsAvailable && !nflFullSeasonPlayer;
};

export const getArrayOfPlayerRecentStats = (playerRecentStats: PlayerRecentStat[] | undefined) => {
    const length = playerRecentStats?.length ?? 0;

    if (length === 0) {
        return [];
    }

    const missingSpots = Math.max(0, MAX_STATS - length);

    return Array(missingSpots)
        .fill(EMPTY_STAT)
        .concat(playerRecentStats ?? []);
};
